import React, { useEffect, useState } from 'react';

import {
  Chip, Skeleton,
} from '@mui/material';

import ContentCard from '../../../../../components/ContentCard';
import ContentGroupsList from '../../../../../components/ContentGroupsList';
import DetailsModal, { Values } from '../../../../../components/DetailsModal';
import { Course } from '../../../../../services/courses/types';
import { useGetStreamsQuery, useNewStreamMutation } from '../../../../../services/streams';
import { Stream } from '../../../../../services/streams/types';

const INITIAL_STREAM_FORM = {
  label: '',
  shortLabel: '',
  importLessons: false,
  scheduleLink: '',
};

interface StreamsCardProps {
  course?: Course
}

const StreamsCard = ({ course }: StreamsCardProps) => {
  const { data: streamsData, isLoading } = useGetStreamsQuery(
    { course: course?.id, includeInactive: true }, { skip: !course },
  );

  // Filter streams based on showInactive state
  const activeStreams = streamsData?.data.filter((s) => s.active === true) || [];

  const [newStream, {
    isSuccess: isNewStreamSuccess,
    isLoading: isNewStreamLoading,
    error: newStreamError,
    reset: resetNewStream,
  }] = useNewStreamMutation();

  const [newStreamForm, setNewStreamForm] = useState(INITIAL_STREAM_FORM);
  const [newStreamFormOpen, setNewStreamFormOpen] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useState<boolean>(true);

  const onStreamCreate = (values?: Values) => {
    if (course?.id) {
      newStream({
        course: course.id,
        attributes: values,
      });
    }
  };

  const resetModal = async () => {
    await resetNewStream();
    setNewStreamForm(INITIAL_STREAM_FORM);
  };

  // Close modal after stream is created
  useEffect(() => {
    if (isNewStreamSuccess) {
      setNewStreamFormOpen(false);
      resetModal();
    }
  }, [isNewStreamSuccess]);

  if (isLoading) {
    return (
      <Skeleton variant="rectangular" height={128} />
    );
  }

  return (
    <ContentCard
      variant="outlined"
      title="Streams"
      buttons={[
        {
          label: showInactive ? 'Hide Inactive' : 'Show Inactive',
          onClick: () => setShowInactive(!showInactive),
        },
      ]}
    >
      <ContentGroupsList
        columns={[
          {
            field: 'label',
            headerName: 'Label',
            width: 360,
          },
          {
            field: 'shortLabel',
            headerName: 'Short Label',
            width: 120,
          }, {
            field: 'active',
            headerName: 'State',
            renderCell: ({ row }: any) => (
              <Chip
                size="small"
                label={row.active ? 'Active' : 'Inactive'}
                color={row.active ? 'success' : 'error'}
              />
            ),
          },
        ]}
        data={showInactive ? streamsData?.data || [] : activeStreams}
        loading={isLoading}
        actions={{
          create: {
            fn: () => setNewStreamFormOpen(true),
            label: 'Add Stream',
          },
        }}
        getRowHref={(row: Stream) => row?.streamId}
      />
      <DetailsModal
        open={newStreamFormOpen}
        handleClose={() => setNewStreamFormOpen(false)}
        error={newStreamError}
        title="Add New Stream"
        fields={{
          label: {
            editable: true,
            type: 'text',
          },
          shortLabel: {
            editable: true,
            type: 'text',
          },
          importLessons: {
            editable: true,
            type: 'select',
            typeOptions: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
          },
          scheduleLink: {
            editable: true,
            type: 'text',
          },
        }}
        values={newStreamForm}
        buttons={[{
          fn: onStreamCreate,
          label: 'Add',
          loading: isNewStreamLoading,
        }]}
      />
    </ContentCard>
  );
};

export default StreamsCard;
